import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import type { StateTree } from '@makemydeal/dr-dash-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';

const HELD = 'held';
const LIVE = 'live';

const isMainPaymentPending = (state: StateTree): boolean =>
    paymentSelectors.isCalculatingPayment(state) ||
    paymentSelectors.isAwaitingFreshPaymentResponse(state) ||
    baseDealSelectors.isAnyMainPaymentInFlight(state);

const getHoldStatus = (state: StateTree): string => {
    if (!featureToggleSelectors.isPreserveFieldsOnPaymentResponseEnabled(state)) return '';
    const isPending = isMainPaymentPending(state) && offerReduxSelectors.isSelectedTermRecordMissing(state);
    return `${isPending ? HELD : LIVE}|${offerReduxSelectors.getCurrentOfferType(state)}|${offerReduxSelectors.getDealXgId(state)}`;
};

type HeldEntry<T> = { value: T; dealKey: string };

export const useHeldValue = <T>(value: T, deal?: unknown, enabled = true): T => {
    const status = useSelector((state: StateTree) => (deal || !enabled ? '' : getHoldStatus(state)));
    const isPending = status.startsWith(HELD);
    const dealKey = status.slice(HELD.length + 1);
    const held = useRef<HeldEntry<T> | null>(null);

    useEffect(() => {
        if (!isPending) {
            held.current = { value, dealKey };
        }
    });

    if (isPending && held.current && held.current.dealKey === dealKey) {
        return held.current.value;
    }
    return value;
};

export const useHeldSelector = <T>(selector: (state: StateTree) => T, equalityFn?: (left: T, right: T) => boolean): T =>
    useHeldValue(useSelector(selector, equalityFn));
